import { interpolateCorankTimelineForReplay } from '../utils/corank-series-to-timeline.util'

// Types, layout constants, and timeline / axis helpers for the co-rank tracker card.
//
// --- Which co-rank row is the “race” about? ---
// A user has multiple rows (per node, plus optional all-public `nodeId: null`). The tracker must use
// the **same row** as the numbers you already show next to each runner (e.g. `userCorank` /
// `myCorank` with the **current app `nodeId`**). When building keyframes from CORANK_UPDATED:
//   • Include only events whose payload matches that row: same `userId` and same `nodeId` as the
//     co-rank scope for that comparison (string match; `null` = all-public row, if that is what the
//     comparison query uses).
// Do not mix node-specific and all-public events in one runner line.
//
// --- Sparse activity (quiet weeks) ---
// Updates only exist when co-rank **changes**. Between updates the score is **unchanged** (flat),
// not a ramp. The card interpolates **linearly** between consecutive keyframes, so either:
//   • Backend supplies step-friendly points (e.g. duplicate value at window boundaries / before the
//     next jump), or
//   • Client switches to “hold until next point” interpolation for race mode.
// Mapping real time into [0, duration]: use one shared window for both runners, e.g.
//   windowStart = min(first included event time for either user, optional lookback floor)
//   windowEnd   = max(last included event time, now)
//   seconds     = clamp(0, duration, (eventTime - windowStart) / (windowEnd - windowStart) * duration)
// If windowEnd === windowStart, collapse to endpoints only. Prefer returning pre-mapped `timeline`
// from GraphQL so filtering, scope, and step semantics stay server-side.

export type CorankTimelinePoint = {
  /** Position on the shared replay clock, 0 … animation duration (e.g. 30s). */
  seconds: number
  /** Co-rank after this keyframe (from CORANK_UPDATED payload / snapshot). */
  corank: number
}

export type CorankTrackerRunner = {
  userName: string
  /** When set with `onRunnerProfilePress` on the card, the runner opens this profile. */
  userId?: string
  /**
   * Co-rank row scope for this comparison (same on all runners from profile hook).
   * Used for `corankUserDifference` and must match timeline queries.
   */
  corankScopeNodeId?: string | null
  /** Final co-rank (end of timeline); also used for axis bounds. */
  corank: number
  /**
   * Optional keyframes; linear segments between points (piecewise “race”).
   * If omitted, timeline is (0, 0) → (duration, corank).
   * When present, build from CORANK_UPDATED timeline events (see file header).
   */
  timeline?: CorankTimelinePoint[]
  isHighlighted?: boolean
}

/** Stable string for detecting co-rank race data changes (e.g. after socket refresh + refetch). */
export function corankRunnersReplayFingerprint(
  runners: CorankTrackerRunner[]
): string {
  return [...runners]
    .map((runner) =>
      [
        runner.userId ?? '',
        String(runner.corank),
        runner.corankScopeNodeId ?? '',
        runner.timeline
          ?.map((point) => `${point.seconds}:${point.corank}`)
          .join(';') ?? '',
      ].join(':')
    )
    .sort()
    .join('|')
}

export type CorankTrackerCardParams = {
  runners: CorankTrackerRunner[]
  showEmptyTrack?: boolean
  corankMin?: number
  corankMax?: number
  labelStep?: number
  pixelsPerCorank?: number
  /** Total timeline length in seconds (default 30). */
  animationDurationSec?: number
  /** When false, hides play bar (track only). */
  showPlaybackBar?: boolean
  /** Open a user's profile when a runner with `userId` is pressed. */
  onRunnerProfilePress?: (params: { userId: string }) => void
  /**
   * When true, the horizontal track auto-scrolls to keep the highlighted runner (viewer) centered
   * while paused at their final co-rank and while the timeline is playing.
   */
  followHighlightedRunner?: boolean
}

export const DIRECTIONS_RUN_PATH =
  'M33.3438 12.0319C35.8646 12.0319 37.9271 9.9694 37.9271 7.44857C37.9271 4.92773 35.8646 2.86523 33.3438 2.86523C30.8229 2.86523 28.7604 4.92773 28.7604 7.44857C28.7604 9.9694 30.8229 12.0319 33.3438 12.0319ZM25.0938 43.8861L27.3854 33.8027L32.1979 38.3861V52.1361H36.7813V34.9486L31.9688 30.3652L33.3438 23.4902C36.3229 26.9277 40.9063 29.2194 45.9479 29.2194V24.6361C41.5938 24.6361 37.9271 22.3444 36.0938 19.1361L33.8021 15.4694C32.8854 14.0944 31.5104 13.1777 29.9063 13.1777C29.2188 13.1777 28.7604 13.4069 28.0729 13.4069L16.1563 18.4486V29.2194H20.7396V21.4277L24.8646 19.8236L21.1979 38.3861L9.96876 36.0944L9.05209 40.6777L25.0938 43.8861Z'

export const AXIS_LABEL_HEIGHT = 22
export const RUNNER_ICON_SIZE = 28
export const RUNNER_COLUMN_WIDTH = 76
export const DEFAULT_LABEL_STEP = 5
export const DEFAULT_PIXELS_PER_CORANK = 14
export const HORIZONTAL_GUTTER = 16
export const INNER_SHADOW_HEIGHT = 14
export const HIGHLIGHT_COLOR = '#4D5C92'
export const HIGHLIGHT_COLOR_ALT = '#7A8BC4'
export const DEFAULT_ANIMATION_DURATION_SEC = 5
/** Slider granularity: steps per second of the timeline (smooth scrub). */
export const SLIDER_STEPS_PER_SECOND = 5
export const TRACK_HEIGHT = 118

export function totalSliderStepsForDuration(durationSec: number): number {
  const durationSeconds = Math.max(durationSec, 1)
  return Math.max(
    DEFAULT_ANIMATION_DURATION_SEC,
    Math.round(durationSeconds * SLIDER_STEPS_PER_SECOND)
  )
}

/**
 * Sorted keyframes, anchored at t=0 and t=durationSec with runner.corank as final value.
 * Finishes with {@link interpolateCorankTimelineForReplay} so slab / step data does not sit flat
 * for most of the replay then jump at the end (many equal co-ranks with staggered timestamps).
 */
export function resolveRunnerTimeline(
  runner: CorankTrackerRunner,
  durationSec: number
): CorankTimelinePoint[] {
  const finalCorank = runner.corank
  const durationSeconds = Math.max(durationSec, 1)

  const baseTimeline = ((): CorankTimelinePoint[] => {
    if (!runner.timeline?.length) {
      return [
        { seconds: 0, corank: 0 },
        { seconds: durationSeconds, corank: finalCorank },
      ]
    }
    const sortedByTime = [...runner.timeline].sort(
      (left, right) =>
        left.seconds - right.seconds || left.corank - right.corank
    )
    const withOrigin =
      sortedByTime[0].seconds > 0
        ? [{ seconds: 0, corank: 0 }, ...sortedByTime]
        : sortedByTime
    const lastPoint = withOrigin[withOrigin.length - 1]
    if (lastPoint.seconds < durationSeconds) {
      return [...withOrigin, { seconds: durationSeconds, corank: finalCorank }]
    }
    return withOrigin.map((point, pointIndex) =>
      pointIndex === withOrigin.length - 1
        ? { ...point, seconds: durationSeconds, corank: finalCorank }
        : point
    )
  })()

  return interpolateCorankTimelineForReplay(
    baseTimeline,
    durationSeconds,
    finalCorank
  )
}

export {
  interpolateCorankSlabSamplesToReplayKeyframes,
  interpolateCorankTimelineForReplay,
} from '../utils/corank-series-to-timeline.util'

/** One edge of the piecewise-linear path: co-rank moves in a straight line between the two keyframes. */
type TimelineSegment = {
  segmentStart: CorankTimelinePoint
  segmentEnd: CorankTimelinePoint
}

/**
 * Sample the animated co-rank at a given clock time (seconds along the shared timeline).
 *
 * **Piecewise linear motion:** between each pair of consecutive keyframes, co-rank changes at a
 * constant rate (that rate differs per segment, so two runners can reach the same co-rank at
 * different times — “slower” vs “faster” animation).
 *
 * **Time clamp:** negative `timeSeconds` is treated as 0 (playback never runs the clock backward
 * past the start here).
 *
 * **Hold before first keyframe:** if time is still at or before the first point’s `seconds`, we
 * return that point’s co-rank (no extrapolation backward in time).
 *
 * **Hold after last keyframe:** if time is past the last point, we return the last keyframe’s
 * co-rank (animation has “finished” at the final value).
 *
 * **Negative co-ranks:** sampled values are not clamped to ≥ 0; the axis should include minima from
 * keyframes via `computeAxisBounds`.
 */
export function corankAtSeconds(
  points: CorankTimelinePoint[],
  timeSeconds: number
): number {
  if (points.length === 0) {
    return 0
  }

  // Clamp time to the timeline ray [0, +∞): scrubbing cannot go before t = 0.
  const timeClamped = Math.max(0, timeSeconds)
  const firstPoint = points[0]
  if (timeClamped <= firstPoint.seconds) {
    // Co-rank is not extrapolated before the first keyframe; snap to the first sample.
    return firstPoint.corank
  }

  // Find the segment (consecutive keyframe pair) whose time interval contains `timeClamped`.
  // Each segment is [segmentStart.seconds, segmentEnd.seconds] with linear interpolation inside.
  const segment = points
    .slice(0, -1)
    .reduce<TimelineSegment | null>((found, segmentStart, segmentIndex) => {
      if (found) {
        return found
      }
      const segmentEnd = points[segmentIndex + 1]
      if (timeClamped <= segmentEnd.seconds) {
        return { segmentStart, segmentEnd }
      }
      return null
    }, null)

  if (!segment) {
    // Past the final keyframe: hold the end co-rank (no extrapolation beyond the timeline).
    return points[points.length - 1].corank
  }

  const spanSeconds = segment.segmentEnd.seconds - segment.segmentStart.seconds
  if (spanSeconds <= 0) {
    // Degenerate segment (two keyframes at the same time, or reversed order): jump to end value.
    return segment.segmentEnd.corank
  }

  // Lerp (linear interpolation): how far we are from start→end of this segment, in 0…1.
  // 0 = exactly at segmentStart; 1 = exactly at segmentEnd.
  const lerpFactor = (timeClamped - segment.segmentStart.seconds) / spanSeconds
  return (
    segment.segmentStart.corank +
    lerpFactor * (segment.segmentEnd.corank - segment.segmentStart.corank)
  )
}

function corankSamplesForAxisBounds(runner: CorankTrackerRunner): number[] {
  const fromTimeline = runner.timeline?.map((point) => point.corank) ?? []
  return [runner.corank, ...fromTimeline]
}

/** Headroom above the highest co-rank seen on finals + timeline keyframes. */
const CORANK_AXIS_MAX_PADDING = 10
/** Extra room below the most negative co-rank (finals + timeline keyframes). */
const CORANK_AXIS_MIN_PADDING = 10
/** Default axis top when there are no runners or max is low. */
const CORANK_AXIS_MAX_FLOOR = 50

export function computeAxisBounds(
  runners: CorankTrackerRunner[],
  corankMin?: number,
  corankMax?: number
) {
  if (corankMin !== undefined && corankMax !== undefined) {
    return { min: corankMin, max: corankMax }
  }
  if (runners.length === 0) {
    return { min: 0, max: CORANK_AXIS_MAX_FLOOR }
  }
  const allSamples = runners.flatMap(corankSamplesForAxisBounds)
  const minSample = Math.min(...allSamples)
  const maxSample = Math.max(...allSamples)
  const axisMin = minSample < 0 ? minSample - CORANK_AXIS_MIN_PADDING : 0
  return {
    min: axisMin,
    max: Math.max(CORANK_AXIS_MAX_FLOOR, maxSample + CORANK_AXIS_MAX_PADDING),
  }
}
